import { FC } from 'react'
import InlineSvg from 'react-inlinesvg'
import { observer } from 'mobx-react-lite'
import { PrismicRichText } from '@prismicio/react'
import { LinkField } from '@prismicio/client'
import tw from 'twin.macro'
import { Plus } from 'react-feather'
import { Body, Grid, Header, SubHeader } from '../UI'
import PrismicLinkInternalOrExternal from '../PrismicLinkInternalOrExternal'
import { RichTextFields } from '../../types/prismic'

import ctaArrow from '../../images/icons/cta-arrow.svg'
import { richTextStrongComponent } from '../../utils/richTextComponents'

type Props = {
  header: string
  subhead: RichTextFields
  steps: Array<{
    step_header: string
    copy: string
  }>
  cta_text: string
  cta_link: LinkField
  gridCols?: 3 | 4
  pagePath: string
  pageTitle: string
}

const AlgorithmSection: FC<Props> = ({
  header,
  subhead,
  steps,
  cta_text,
  cta_link,
  gridCols = 3,
  pagePath,
  pageTitle,
}) => {
  return (
    <section tw="bg-gb-blue pt-[40px] pb-[60px] lg:(pt-[80px] pb-[100px]) text-white font-domaine overflow-hidden relative">
      <Grid>
        {/* Headline content */}
        <div tw="col-span-full lg:col-span-11">
          <h2 css={[Body, tw`mb-[15px] lg:mb-[25px]`]}>{header}</h2>
          <h3 css={Header}>
            <PrismicRichText
              field={subhead.richText}
              components={richTextStrongComponent}
            />
          </h3>
        </div>
        {/* Steps */}
        {steps && (
          <div
            css={[
              tw`col-span-full grid gap-16 my-[40px]`,
              gridCols === 3 && tw`lg:(grid-cols-3 px-16 my-[60px])`,
              gridCols === 4 && tw`lg:(grid-cols-4 px-8 my-[60px])`,
            ]}
          >
            {steps.map((el, index) => {
              const isLast = index === steps.length - 1
              return (
                <div
                  key={index}
                  css={[
                    tw`relative lg:(border-b-0 pb-0 border-l pl-4)`,
                    !isLast && tw`pb-4 border-b`,
                  ]}
                >
                  {index > 0 && (
                    <Plus tw="w-4 absolute top-[-40px] left-0 lg:(top-[40%] left-[-30px])" />
                  )}
                  <div>
                    <h4 css={[SubHeader, tw`mb-4`]}>{el.step_header}</h4>
                    <p>{el.copy}</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <div tw="col-span-full lg:(flex justify-center)">
          {!!cta_text && !!cta_link && (
            <PrismicLinkInternalOrExternal
              field={cta_link}
              trackEventParams={{
                pageTitle: pageTitle,
                path: pagePath,
                target: 'url' in cta_link ? cta_link.url : '',
                copy: cta_text,
              }}
              cssStyle={tw`uppercase pt-2 pb-1 font-object text-[13px] line-height[13px] letter-spacing[1px] border-b border-white lg:(transition-colors hover:bg-gb-blue-dark)`}
            >
              {cta_text}
              <InlineSvg
                src={ctaArrow}
                tw="w-6 ml-[5px] mt-[-2px] inline text-white"
              />
            </PrismicLinkInternalOrExternal>
          )}
        </div>
      </Grid>
    </section>
  )
}

export default observer(AlgorithmSection)
